/**
 * Unified image library — single home for every image-related primitive in the
 * app. Display, upload, preview, validation, and lightbox all live here.
 * Direct imports of `next/image` and `uploadImageToIPFS` outside this folder
 * are blocked by precommit checks FQ-IMG-001 / FQ-IMG-003. Direct imports of
 * `@/lib/pattern-studio` are blocked outside `optimized-image.tsx` itself by
 * FQ-IMG-LIB-FACADE-001.
 *
 * Layers (composition order):
 *   1.  `validation.ts`              — shared upload limits + file validator.
 *   2.  `optimized-image.tsx`        — the canonical `<Image>` wrapper and
 *       single frontend façade for `@/lib/pattern-studio`.
 *   3.  `crop-modal.tsx`             — single confirm/crop/upload modal.
 *   4.  `lightbox-modal.tsx`         — full-screen image preview overlay.
 *   5.  `user-avatar.tsx`            — user-identity avatar display + initials.
 *   6.  `hero-banner.tsx`            — user-identity 3:1 cover banner display.
 *   7.  `identity-picker.tsx`        — shared core for avatar + hero pickers
 *       (Search / Pattern / Upload tabs). Drives both via a `kind` prop.
 *   8.  `avatar-picker.tsx` / `hero-picker.tsx` — thin wrappers around the
 *       identity picker.
 *   9.  `editable-avatar.tsx` / `editable-hero.tsx` — click-to-edit triggers.
 *   10. `market-picker.tsx`          — Search / AI-generate / Upload tabs for
 *       market cover + outcome thumbnails (used inline in the create-proposal
 *       wizard cover step).
 *   11. `market-picker-modal.tsx`    — dialog wrapper around `<MarketImagePicker>`
 *       for the outcome thumbnail trigger in `<OutcomeInput>`.
 *   12. `search-tab.tsx` / `search-grid.tsx` — shared image-search surface
 *       used by `identity-picker.tsx`. `market-picker.tsx` renders
 *       `<SearchGrid>` directly (it owns its own crop view inline).
 *   13. `image-upload-dropzone.tsx`   — shared dashed-border upload dropzone
 *       (aspect-square by default; consumed by `identity-picker` Upload tab
 *       and `market-picker` Upload tab).
 */

export { AvatarPicker } from './avatar-picker';
export type {
  CropModalProps,
  ImageCropConfig,
  ImagePresetGroup,
} from './crop-modal';
// Modal + helpers
export { CropModal } from './crop-modal';
// Editable trigger components
export { EditableAvatar } from './editable-avatar';
export { EditableHero } from './editable-hero';
export type { GeneratingFrameProps } from './generating-frame';
export { GeneratingFrame } from './generating-frame';
export { HeroBanner } from './hero-banner';
export { HeroPicker } from './hero-picker';
export { ImageUploadDropzone } from './image-upload-dropzone';
export { LightboxModal } from './lightbox-modal';
// Display primitives
export { OptimizedImage } from './optimized-image';
export type { UserAvatarSize } from './user-avatar';
export { UserAvatar } from './user-avatar';
export { hashDisplayName } from './user-avatar-utils';
export type { ImageUploadValidationError } from './validation';
export {
  IMAGE_UPLOAD_ACCEPT_ATTR,
  IMAGE_UPLOAD_ALLOWED_TYPES,
  IMAGE_UPLOAD_MAX_BYTES,
  IMAGE_UPLOAD_MAX_MB,
  validateImageUpload,
} from './validation';
